import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Mail, Copy, CheckCircle2, Send, Clock, Search } from "lucide-react";

const ROLE_META = {
    super_admin: { label: "Super Admin", color: "#0A0A0A", desc: "Full control · invite users · manage everything" },
    sales: { label: "Sales · Executive", color: "#16A34A", desc: "View all systems · send performance reports" },
    customer: { label: "Customer", color: "#6B7280", desc: "View-only access to their own plants" },
};

const DEFAULT_MESSAGE = (name, role, tempPw) => `Hi ${name || "there"},

You have been invited to the RPPL Solar PV Monitoring System as a ${role.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}.

Sign in at your RPPL SolarOS console with:
  Email: (your email above)
  Temporary password: ${tempPw || "(shown after creation)"}

You will be able to change your password after signing in.

Regards,
RPPL Solar Operations`;

export default function InviteUsers() {
    const [plants, setPlants] = useState([]);
    const [history, setHistory] = useState([]);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("sales");
    const [selectedStations, setSelectedStations] = useState([]);
    const [customPw, setCustomPw] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [q, setQ] = useState("");

    const loadHistory = () => api.get("/admin/invites").then(r => setHistory(r.data)).catch(() => {});
    useEffect(() => {
        api.get("/plants").then(r => setPlants(r.data));
        loadHistory();
    }, []);

    const toggleStation = (id) => {
        setSelectedStations(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const body = { email, name, role };
            if (role === "customer") body.allowed_station_ids = selectedStations;
            if (customPw.trim()) body.password = customPw.trim();
            const { data } = await api.post("/admin/users/invite", body);
            setLastResult(data);
            toast.success(`Invitation sent to ${email}`);
            loadHistory();
            // reset form (except last result stays)
            setEmail(""); setName(""); setSelectedStations([]); setCustomPw(""); setMessage("");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const copy = (v, what = "Value") => {
        navigator.clipboard.writeText(v);
        toast.success(`${what} copied to clipboard`);
    };

    const filteredHist = history.filter(h =>
        !q || h.to_email.toLowerCase().includes(q.toLowerCase()) || h.name.toLowerCase().includes(q.toLowerCase())
    );

    const canSubmit = email && name && (role !== "customer" || selectedStations.length > 0);

    return (
        <div className="space-y-6" data-testid="invite-users-page">
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Admin Console</div>
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
                    <UserPlus className="w-6 h-6 text-[#0A0A0A]" /> Invite Users
                </h1>
                <p className="text-sm text-[#6B7280] mt-1">Send invitations to team members and customers. Each invite creates an account and dispatches an onboarding email.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Invite form */}
                <form onSubmit={submit} className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5" data-testid="invite-form">
                    <div className="flex items-center gap-2">
                        <Send className="w-4 h-4 text-[#0A0A0A]" />
                        <div className="text-sm font-semibold text-[#111827]">New Invitation</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Full Name</label>
                            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Vinoth Kumar" className="rounded-md" data-testid="invite-name" required />
                        </div>
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Email Address</label>
                            <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="user@example.com" className="rounded-md" data-testid="invite-email" required />
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Role</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {Object.entries(ROLE_META).map(([k, v]) => (
                                <button
                                    type="button"
                                    key={k}
                                    onClick={() => setRole(k)}
                                    data-testid={`role-btn-${k}`}
                                    className={`text-left p-3 rounded-md border transition-all duration-200 ${role === k ? "border-transparent shadow-sm" : "border-[#E5E7EB] hover:border-[#D1D5DB]"}`}
                                    style={role === k ? { background: `${v.color}12`, borderColor: `${v.color}66`, boxShadow: `0 0 0 1px ${v.color}44` } : {}}
                                >
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.color }} />
                                        <span className="text-sm font-medium" style={{ color: role === k ? v.color : "#111827" }}>{v.label}</span>
                                    </div>
                                    <div className="text-[10px] text-[#6B7280] leading-tight">{v.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {role === "customer" && (
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                Plant Access <span className="text-[#0A0A0A] normal-case tracking-normal">({selectedStations.length} selected)</span>
                            </label>
                            <div className="max-h-48 overflow-y-auto border border-[#E5E7EB] rounded-md divide-y divide-[#F3F4F6]" data-testid="station-picker">
                                {plants.map(p => (
                                    <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#F9FAFB] cursor-pointer">
                                        <Checkbox checked={selectedStations.includes(p.id)} onCheckedChange={() => toggleStation(p.id)} data-testid={`station-cb-${p.id}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-[#111827] truncate">{p.name}</div>
                                            <div className="text-xs text-[#6B7280]">{p.address}</div>
                                        </div>
                                        <div className="text-xs font-mono text-[#6B7280]">{p.installed_capacity_kw} kWp</div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                Temporary Password <span className="text-zinc-400 normal-case tracking-normal">(blank = auto)</span>
                            </label>
                            <Input value={customPw} onChange={e => setCustomPw(e.target.value)} placeholder="auto-generated" className="rounded-md font-mono" data-testid="invite-password" />
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                            Message <span className="text-zinc-400 normal-case tracking-normal">(optional — added to email)</span>
                        </label>
                        <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder={DEFAULT_MESSAGE(name, role, "auto-generated")} className="rounded-md text-sm" data-testid="invite-message" />
                    </div>

                    <div className="pt-2 flex items-center justify-between gap-3">
                        <div className="text-[10px] text-[#6B7280] leading-relaxed max-w-md">
                            The invite is dispatched instantly via Resend to the user's email with their temporary password. Delivery status is logged below.
                        </div>
                        <Button type="submit" disabled={loading || !canSubmit} className="bg-[#0A0A0A] hover:bg-[#000000] text-white rounded-md h-10 shadow-[0_2px_8px_rgba(249,115,22,0.25)]" data-testid="invite-submit">
                            {loading ? "Sending…" : <><Send className="w-4 h-4 mr-2" /> Send Invitation</>}
                        </Button>
                    </div>
                </form>

                {/* Last result / preview panel */}
                <div className="space-y-4">
                    {lastResult ? (
                        <div className="bg-[#0B0D11] rounded-lg overflow-hidden" data-testid="invite-result">
                            <div className="p-4 border-b border-white/10">
                                <div className="flex items-center gap-2 text-[#16A34A] text-sm font-medium">
                                    <CheckCircle2 className="w-4 h-4" /> Invitation Sent
                                </div>
                                <div className="text-xs text-zinc-500 mt-1">Delivered via Resend</div>
                            </div>
                            <div className="p-4 space-y-4">
                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Recipient</div>
                                    <div className="text-sm text-white">{lastResult.user.name}</div>
                                    <div className="text-xs font-mono text-zinc-400">{lastResult.user.email}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Temporary Password</div>
                                    <div className="flex gap-2">
                                        <div className="flex-1 font-mono text-sm text-[#0A0A0A] bg-black/40 px-3 py-2.5 rounded-md border border-white/5">
                                            {lastResult.temporary_password}
                                        </div>
                                        <Button type="button" onClick={() => copy(lastResult.temporary_password, "Password")} variant="outline" className="rounded-md bg-white/5 border-white/10 text-white hover:bg-white/10" data-testid="copy-pw-btn">
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Email Subject</div>
                                    <div className="text-xs text-zinc-300 bg-black/40 px-3 py-2 rounded-md border border-white/5">
                                        {lastResult.invite_email?.subject}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white border border-dashed border-[#E5E7EB] rounded-lg p-6 text-center">
                            <Mail className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                            <div className="text-sm text-[#111827] font-medium">Nothing sent yet</div>
                            <div className="text-xs text-[#6B7280] mt-1">Submit the form to invite your first user. The temporary password will appear here.</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Invitation history */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg">
                <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#6B7280]" />
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Invitation History ({history.length})</div>
                            <div className="text-sm text-[#111827] mt-0.5">All invites dispatched from this console</div>
                        </div>
                    </div>
                    <div className="relative max-w-xs">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search invites…" className="pl-9 rounded-md" data-testid="invites-search" />
                    </div>
                </div>
                {filteredHist.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#6B7280]">No invitations {q ? "match your search" : "have been sent yet"}.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="invites-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-[#6B7280] bg-[#F9FAFB] border-b border-[#E5E7EB]">
                                <th className="py-2.5 px-4 text-left font-semibold">Sent</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Recipient</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Role</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Invited by</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredHist.map((h, i) => {
                                const rm = ROLE_META[h.role] || {};
                                return (
                                    <tr key={h.id || i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors" data-testid={`invite-row-${i}`}>
                                        <td className="py-2.5 px-4 font-mono text-xs text-[#6B7280]">{h.sent_at?.slice(0, 16).replace('T', ' ')}</td>
                                        <td className="py-2.5 px-4">
                                            <div className="text-[#111827] text-sm">{h.name}</div>
                                            <div className="text-xs font-mono text-[#6B7280]">{h.to_email}</div>
                                        </td>
                                        <td className="py-2.5 px-4">
                                            <span className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded-full" style={{ background: `${rm.color}18`, color: rm.color, border: `1px solid ${rm.color}44` }}>
                                                <span className="w-1 h-1 rounded-full" style={{ background: rm.color }} />
                                                {rm.label}
                                            </span>
                                        </td>
                                        <td className="py-2.5 px-4 text-xs text-[#6B7280] font-mono">{h.invited_by}</td>
                                        <td className="py-2.5 px-4">
                                            <span className="text-xs font-mono text-[#0A0A0A]">{h.status}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
